import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { CommentModel } from '@fi-shared/models/comment.model';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FormValidatorService } from '@fi-app/utilities/fi-services/form-validator.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ChecklistService } from '../checklist.service';
import { isDefined } from '@fi-shared/utils/is-defined';
import { isEqual } from '@fi-shared/utils/is-equal';
import { cloneDeep } from '@fi-shared/utils/clone-deep';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { ErrorMessageService } from '@fi-app/utilities/fi-services/error-message.service';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { SystemNotificationsService } from '@fi-app/system-notifications/system-notifications.service';
import { CheckpointStatusType, IFiChecklistCheckpointModel } from '@fi-shared/checklist';
import { IFiChecklistModelBase, IFiChecklistViewerModel } from '@fi-shared/checklist';
import { Subject } from 'rxjs';
import { DocumentDialogAbsentContentLanguageComponent } from '@fi-app/document/dialog-absent-content-language/dialog-absent-content-language.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { FiChecklistService } from '@fi-shared/fi-shared-services';
import { FiChecklistCheckpointViewComponent } from '@fi-app/checklist/checkpoint-view/checkpoint-view.component';
import { ICheckPointNotApplicableItem } from '@fi-shared/checklist/checklist';
import { createMap } from '@fi-shared/utils/create-map';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';

interface ICheckpointSection {
    tag: {
        Id: number;
        Name: string;
    };
    CheckPoints: IFiChecklistCheckpointModel[];
}

@Component({
    selector: 'fi-checklist-view',
    templateUrl: './checklist-view.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./checklist-view.component.scss'],
    host: { class: 'fi-checklist-view' },
    standalone: false,
})
export class FiChecklistViewComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() checklist: IFiChecklistModelBase;
    @Input() checklistViewer: IFiChecklistViewerModel;
    @Input() unlockedCheckpoint;
    @Input() fromSystemNotifications: boolean;

    @ViewChild('ChecklistViewForm', { static: true }) ChecklistViewForm: NgForm;
    @ViewChild('fiContentElement', { static: true, read: ElementRef }) fiContentElementRef: ElementRef;

    @ViewChildren(FiChecklistCheckpointViewComponent)
    checkpointViewComponents: QueryList<FiChecklistCheckpointViewComponent>;

    moreMenuActions = [];
    selectedLanguageId: number;

    private notApplicableInitialData: {
        [prop: number]: boolean;
    } = {};

    private changedCheckpointIds: number[] = [];

    constructor(
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private formValidatorService: FormValidatorService,
        private loadingService: LoadingService,
        private checklistService: ChecklistService,
        private fiChecklistService: FiChecklistService,
        private saveChangesWarningService: SaveChangesWarningService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private errorMessageService: ErrorMessageService,
        private stateService: FiStateService,
        private elementRef: ElementRef,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private systemNotificationsService: SystemNotificationsService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private contentLanguagesService: ContentLanguagesService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private userDataService: FiUserDataService,
        private cdr: ChangeDetectorRef,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    onSetDirty(checkpoint: IFiChecklistCheckpointModel) {
        this.ChecklistViewForm.control.markAsDirty();
        this.updateProgressBarValues();
        if (!this.changedCheckpointIds.includes(checkpoint.Id)) {
            this.changedCheckpointIds.push(checkpoint.Id);
        }
    }

    isChecklistNotApplicable(): boolean {
        return this.checklist.IsSetNotApplicable;
    }

    setApplicableStatus() {
        const allowedActions = this.checklistService.getUserAllowedActions();
        if (!allowedActions.setNotApplicable.isDisabled)
            allowedActions.setNotApplicable.onClick(this.checklist as ChecklistModel);
    }

    ngOnInit() {
        const userPreferredContentLanguageId = this.userDataService.userData.PreferredContentLanguage;
        const defaultContentLanguageId = this.contentLanguagesService.GetDefaultLanguageId();
        const checklistLanguageIds = this.getChecklistLangIds();
        if (checklistLanguageIds.indexOf(userPreferredContentLanguageId) !== -1) {
            this.selectedLanguageId = userPreferredContentLanguageId;
        } else if (checklistLanguageIds.indexOf(defaultContentLanguageId) !== -1) {
            this.selectedLanguageId = defaultContentLanguageId;
        } else if (checklistLanguageIds.length === 1) {
            this.selectedLanguageId = checklistLanguageIds[0];
        } else {
            this.handleNoDefaultTranslation(checklistLanguageIds);
        }

        this.iniChecklistViewer();

        this.checklist.CheckPoints.forEach((checkpoint) => {
            this.notApplicableInitialData[checkpoint.Id] = isSetNotApplicationParser(checkpoint) || false;
        });

        const allowedActions = this.checklistService.getUserAllowedActions();
        this.moreMenuActions = [
            allowedActions.editChecklist,
            allowedActions.viewReports,
            allowedActions.saveAsTemplate,
            allowedActions.turnOffReminder,
            allowedActions.setNotApplicable,
            allowedActions.deleteResponses,
            allowedActions.deleteChecklist,
        ];

        this.moreMenuActions = this.moreMenuActions.filter((item) => {
            return !item.condition || item.condition(this.checklist);
        });

        allowedActions.deleteChecklist.onClick = ((deleteChecklist) => {
            return () => {
                return deleteChecklist(this.checklist).then(() => {
                    this.returnToList();
                });
            };
        })(allowedActions.deleteChecklist.onClick);

        const getChangesModel = () => {
            return {
                ChecklistViewForm: this.ChecklistViewForm ? this.ChecklistViewForm.pristine : true,
            };
        };

        this.saveChangesWarningService.start(getChangesModel);
        this.updateProgressBarValues();
        this.buildSections(this.checklist);
        this.fiChecklistService.subscribeOnChecklistAnswersChange({
            checkListId: this.checklistViewer.CheckListId,
            periodId: this.checklistViewer.PeriodId,
            businessUnitId: this.checklistViewer.BusinessUnitId,
            answers: this.checklistViewer.Answers,
            ngOnDestroy$: this.ngOnDestroy$,
            afterChange: () => {
                const checkpointViewComponentsArray = this.checkpointViewComponents.toArray();
                checkpointViewComponentsArray.forEach((checkpointViewComponent) =>
                    checkpointViewComponent.onChecklistAnswersChanged(),
                );
                this.updateProgressBarValues();
                this.cdr.detectChanges();
            },
        });
    }

    ngAfterViewInit() {
        if (this.unlockedCheckpoint) {
            const elements: HTMLElement[] = Array.from(
                this.elementRef.nativeElement.querySelectorAll('.unlocked-checkpoint'),
            );
            if (elements.length > 0) {
                setTimeout(() => {
                    this.scrollToElementAnimateService.scrollToElementBottom(
                        elements[0],
                        this.fiContentElementRef.nativeElement,
                        20,
                    );
                }, 0);
            }
        }

        this.checklistService.markChecklistAsVisited(this.checklist.Id);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    handleNoDefaultTranslation(availableIds: number[]) {
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('common.oops'),
                yesButtonText: this.appTextService.get('document.view_page'),
                contentComponentRef: DocumentDialogAbsentContentLanguageComponent,
                contentComponentData: {
                    langIds: availableIds,
                    messageText: this.appTextService.get('document.dialog_absent_content_language_text'),
                },
                config: {
                    panelClass: 'dialog-content-padding',
                },
            })
            .onYesButtonClick((result) => (this.selectedLanguageId = result))
            .onNoButtonClick(() => window.history.back());
    }

    getChecklistName(): string {
        return this.fiContentLanguageSharedService.getContentLanguageValueForDisplayContent(
            this.checklist.Names,
            this.selectedLanguageId,
        );
    }

    getChecklistLangIds(): number[] {
        return this.checklist.CheckPoints[0].Names.filter((name) => name.Value).map((item) => item.ContentLanguageId);
    }

    iniChecklistViewer() {
        if (this.checklistViewer) {
            const answersMap = createMap(this.checklistViewer.Answers, 'CheckPointId');
            this.checklist.CheckPoints.forEach((checkpoint) => {
                checkpoint.$$fiCheckpointAnswer = answersMap[checkpoint.Id];
            });

            this.checklistViewer.Answers.forEach((answer) => {
                answer.$$answerInitial = answer.Answer;
                answer.$$commentContentInitial = answerCommentContentParser(answer);
                answer.$$mediaFileReferenceInitial = answerMediaFileReferenceParser(answer);
                if (!answer.Id && answer.Answer) {
                    answer.$$answerInitial = null;
                    setTimeout(() => {
                        this.ChecklistViewForm.control.markAsDirty();
                    });
                }

                if (answer.Comments.length === 0) {
                    const newComment = new CommentModel(this.fiFormatDateService);
                    newComment.ContentLanguageId = this.userDataService.userData.PreferredContentLanguage;
                    answer.Comments.push(newComment);
                }
            });
        }
    }

    doneClick($event: Event) {
        this.ChecklistViewForm.onSubmit($event);
        if (this.ChecklistViewForm.invalid) {
            this.formValidatorService.showErrorMessage();
            return false;
        }

        this.loadingService.startLoading();
        const checklistViewerToSave: IFiChecklistViewerModel = cloneDeep(this.checklistViewer),
            answers = checklistViewerToSave.Answers;
        // save only changed answers
        checklistViewerToSave.Answers = [];
        answers.forEach((answer) => {
            if (this.changedCheckpointIds.includes(answer.CheckPointId)) {
                checklistViewerToSave.Answers.push(answer);
            }
            /*if (answer.Answer !== answer.$$answerInitial ||
                answerCommentContentParser(answer) !== answer.$$commentContentInitial ||
                answerMediaFileReferenceParser(answer) !== answer.$$mediaFileReferenceInitial) {
                checklistViewerToSave.Answers.push(answer);
            }*/
        });
        this.checklistService
            .saveCheckListViewer(checklistViewerToSave)
            .then(() => {
                const promisePull: Promise<void>[] = [];
                const notApplicableData: ICheckPointNotApplicableItem[] = [];
                this.checklist.CheckPoints.forEach((checkpoint) => {
                    const isSetNotApplicable = isSetNotApplicationParser(checkpoint);
                    if (!isDefined(isSetNotApplicable)) {
                        return;
                    }

                    if (!isEqual(this.notApplicableInitialData[checkpoint.Id], isSetNotApplicable)) {
                        notApplicableData.push({
                            CheckPointId: checkpoint.Id,
                            IsNotApplicable: isSetNotApplicable,
                        });
                    }
                });

                if (notApplicableData.length) {
                    promisePull.push(this.fiChecklistService.setCheckPointsNotApplicable(notApplicableData));
                }

                Promise.all(promisePull)
                    .then(() => {
                        this.loadingService.finishLoading();
                        this.saveChangesWarningService.stop();
                        this.mobileAppHelperService.reloadPriorityFeed();
                        if (!this.mobileAppHelperService.mobileAppMode) {
                            if (!this.fromSystemNotifications) {
                                this.returnToList();
                            } else {
                                // https://chainformation.atlassian.net/browse/FI-2701
                                window.history.back();
                                this.systemNotificationsService.opened = true;
                            }
                        } else {
                            this.mobileAppHelperService.reloadPriorityFeed();
                            this.mobileAppHelperService.closeIFrame();
                        }
                    })
                    .catch(() => {
                        this.loadingService.finishLoading();
                    });
            })
            .catch(() => {
                this.loadingService.finishLoading();
            });
    }

    isDoneDisabled() {
        return this.ChecklistViewForm.pristine;
    }

    isChecklistActive() {
        return checklistIsActiveParser(this.checklistViewer);
    }

    isCheckpointMenuDisabled(checkpoint) {
        return (
            (!this.checklistService.isEditAllowedByModuleRights() &&
                (!checkpointAnswerParser(checkpoint) ||
                    !checklistIsActiveParser(this.checklistViewer) ||
                    isSetNotApplicationParser(checkpoint))) ||
            this.checklist.IsSetNotApplicable
        );
    }

    isAnswersDisabled(checkpoint) {
        return (
            !this.checklistViewer ||
            !this.checklistViewer.IsActive ||
            this.checklist.IsSetNotApplicable ||
            isSetNotApplicationParser(checkpoint)
        );
    }

    isAnswerDisabled(checkpoint) {
        return this.isAnswersDisabled(checkpoint) || isSetNotApplicationParser(checkpoint);
    }

    isAddFileDisabled(checkpoint) {
        return this.isAnswerDisabled(checkpoint) || !checkpoint.$$fiCheckpointAnswer.Answer;
    }

    returnToList() {
        // https://chainformation.atlassian.net/browse/FI-1749
        const filterTypes = [];
        if (!this.mobileAppHelperService.mobileAppMode) {
            filterTypes.push(SideListOptionType.SharedWithMe);
        }
        if (this.stateService.params.filterType != SideListOptionType.SharedWithMe) {
            filterTypes.push(SideListOptionType.CurrentCheckLists);
            if (this.stateService.params.filterType != SideListOptionType.CurrentCheckLists) {
                filterTypes.push(this.stateService.params.filterType);
            }
        }

        const returnToListInternal = (filterTypesQueue) => {
            const filterType = filterTypesQueue.pop();
            if (!filterTypesQueue.length) {
                this.stateService.go(FiStateNames.Checklist.List, { filterType: filterType });
            } else {
                const promise = this.checklistService.getCheckListsCount({
                    FilterType: filterType,
                });
                promise.then((data) => {
                    if (data > 0) {
                        this.stateService.go(FiStateNames.Checklist.List, { filterType: filterType });
                    } else {
                        returnToListInternal(filterTypesQueue);
                    }
                });
            }
        };

        returnToListInternal(filterTypes);
    }

    checkpointAnswerValueOnChange(checkpoint: IFiChecklistCheckpointModel) {
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    totalCheckpoints = 0;
    answeredCheckpoints = 0;

    private updateProgressBarValues() {
        this.totalCheckpoints = this.checklist.CheckPoints.filter(function (checkpoint) {
            return !isSetNotApplicationParser(checkpoint);
        }).length;

        this.answeredCheckpoints = this.checklist.CheckPoints.filter(function (checkpoint) {
            /*
            WHEN user marks a checkpoint as not applicable.
            OR user unmarks a checkpoint as not applicable.

            THEN then this must be taken into consideration when showing the checklist completion and we have to re-calculate the completion percentage.
             */
            if (isSetNotApplicationParser(checkpoint)) {
                return false;
            }

            /*
            If a checkpoint has commentary AND/OR attachment as a mandatory option.
            Then this must also be added to the checkpoint before it's considered "complete".
             */

            const answerValue = checkpointAnswerParser(checkpoint),
                attachmentValue = checkpointAnswerMediaFileReferenceParser(checkpoint);

            if (
                !attachmentValue &&
                ((checkpoint.IsAttachmentMandatoryOk && answerValue === CheckpointStatusType.Ok) ||
                    (checkpoint.IsAttachmentMandatoryNotOk && answerValue === CheckpointStatusType.NotOk))
            ) {
                return false;
            }

            const commentValue = checkpointAnswerCommentParser(checkpoint);

            if (
                !(commentValue && commentValue.Content && commentValue.Content.length > 0) &&
                ((checkpoint.IsCommentMandatoryOk && answerValue === CheckpointStatusType.Ok) ||
                    (checkpoint.IsCommentMandatoryNotOk && answerValue === CheckpointStatusType.NotOk))
            ) {
                return false;
            }

            return answerValue;
        }).length;
    }

    onAddMedia(checkpoint: IFiChecklistCheckpointModel) {
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    onMediaRemove(checkpoint: IFiChecklistCheckpointModel) {
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    onCheckpointNotApplicationToggle(checkpoint: IFiChecklistCheckpointModel) {
        this.onSetDirty(checkpoint);
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    onCheckpointAnswerCleared(checkpoint: IFiChecklistCheckpointModel) {
        //this.onSetDirty(checkpoint);
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    onCheckpointCommentChange(checkpoint: IFiChecklistCheckpointModel) {
        //this.onCheckpointAnswerChanged(checkpoint);
    }

    fiChecklistSections: ICheckpointSection[] = [];

    buildSections(checklist: IFiChecklistModelBase, sortNotApplicable = true) {
        this.fiChecklistSections = [];
        const tagIds = [];
        let lastSection = createEmptySection();
        checklist.CheckPoints.forEach((value) => {
            if (value.TagId && lastSection.tag.Id !== value.TagId) {
                lastSection = {
                    tag: value.Tag,
                    CheckPoints: [],
                };
            }

            if (tagIds.indexOf(lastSection.tag.Id) === -1) {
                this.fiChecklistSections.push(lastSection);
                tagIds.push(lastSection.tag.Id);
            }

            lastSection.CheckPoints.push(value);
        });

        if (sortNotApplicable) {
            // https://chainformation.atlassian.net/browse/FI-1684
            // not applicable checkpoint should be at the bottom
            this.fiChecklistSections.forEach((section) => {
                section.CheckPoints = section.CheckPoints.sort((a, b) => {
                    const aIsSetNotApplicable = isSetNotApplicationParser(a),
                        bIsSetNotApplicable = isSetNotApplicationParser(b);
                    if (aIsSetNotApplicable && !bIsSetNotApplicable) return 1;
                    if (!aIsSetNotApplicable && bIsSetNotApplicable) return -1;
                    const aIdx = checklist.CheckPoints.indexOf(a),
                        bIdx = checklist.CheckPoints.indexOf(b);
                    return aIdx - bIdx;
                });
            });
        }
    }
}

function isSetNotApplicationParser(checkpoint: IFiChecklistCheckpointModel) {
    return !!(checkpoint && checkpoint.CheckPointSetting && checkpoint.CheckPointSetting.IsSetNotApplicable);
}

function checkpointAnswerParser(checkpoint) {
    if (checkpoint && checkpoint.$$fiCheckpointAnswer) {
        return checkpoint.$$fiCheckpointAnswer.Answer;
    }
}

function checklistIsActiveParser(checklist) {
    return checklist.IsActive;
}

function answerCommentContentParser(answer) {
    if (answer && answer.Comments && answer.Comments.length > 0) {
        return answer.Comments[0].Content;
    }
}

function answerMediaFileReferenceParser(answer) {
    if (answer && answer.MediaBankFileReferences && answer.MediaBankFileReferences.length > 0) {
        return answer.MediaBankFileReferences[0];
    }
}

function checkpointAnswerMediaFileReferenceParser(checkpoint) {
    if (
        checkpoint &&
        checkpoint.$$fiCheckpointAnswer &&
        checkpoint.$$fiCheckpointAnswer.MediaBankFileReferences &&
        checkpoint.$$fiCheckpointAnswer.MediaBankFileReferences.length > 0
    ) {
        return checkpoint.$$fiCheckpointAnswer.MediaBankFileReferences[0];
    }
}

function checkpointAnswerCommentParser(checkpoint) {
    if (
        checkpoint &&
        checkpoint.$$fiCheckpointAnswer &&
        checkpoint.$$fiCheckpointAnswer.Comments &&
        checkpoint.$$fiCheckpointAnswer.Comments.length > 0
    ) {
        return checkpoint.$$fiCheckpointAnswer.Comments[0];
    }
}

function createEmptySection(): ICheckpointSection {
    return {
        tag: {
            Id: null,
            Name: null,
        },
        CheckPoints: [],
    };
}
